// Formatting a record field for a member. Field types come from the company's Data Fields catalog via the group's
// portal_records config; anything the type doesn't settle is shown as text.

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatDate = (value) => {
  // A bare date has no time zone: build it in local time so 2026-10-14 can't render as Oct 13 west of UTC.
  const match = ISO_DATE.exec(value);
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const formatDateTime = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const EMPTY_VALUE = '—';

// The catalog types these as plain numbers, so their ids decide how they read. A year never gets a thousands
// separator ("2,019"); matches year, normalized_year and matching_vehicle__year.
const YEAR_FIELD = /(^|_)year$/;
// Amounts read as money ("$176.93", not "176.93"): transaction totals and line-item / recommendation prices.
const MONEY_FIELD = /^(total|subtotal|price_total|price_unit|unit_price)$/;
// Yes/no values that POS systems send as text. WHY: any non-empty string is truthy, so "false" showed as "Yes".
const FALSE_TEXT = ['false', 'no', 'n', 'f', '0'];

const isYes = (value) => (typeof value === 'string' ? !FALSE_TEXT.includes(value.trim().toLowerCase()) : Boolean(value));

/**
 * @param {*} value - as returned by the records endpoint
 * @param {string} [type] - catalog field type: text, number, boolean, date, datetime
 * @param {string} [fieldId] - catalog field id, for the few fields whose id says more than their type
 */
const formatFieldValue = (value, type, fieldId) => {
  if (value === null || value === undefined || value === '') return EMPTY_VALUE;
  if (type === 'boolean' || typeof value === 'boolean') return isYes(value) ? 'Yes' : 'No';
  if (type === 'datetime' || (typeof value === 'string' && ISO_DATETIME.test(value))) return formatDateTime(value);
  if (type === 'date' || (typeof value === 'string' && ISO_DATE.test(value))) return formatDate(value);
  if (type === 'number' || typeof value === 'number') {
    const number = Number(value);
    if (Number.isNaN(number)) return String(value);
    // The portal only receives the fields the company picked, so it has no currency field to read; Cinch amounts
    // are US dollars.
    if (MONEY_FIELD.test(fieldId || '')) return number.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
    return number.toLocaleString(undefined, { maximumFractionDigits: 2, useGrouping: !YEAR_FIELD.test(fieldId || '') });
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export default formatFieldValue;
